import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
  TableContainer,
  Grid,
  FormControl,
  InputLabel,
  Stack,
  Autocomplete,
} from "@mui/material";

import {
  Edit,
  CheckCircle,
  Cancel,
  Add,
  Delete,
  Close,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CandidateSkillAction } from "../../../Redux/action/CandidateSkillAction";
import { AddSkillsAction } from "../../../Redux/action/AddSkillsAction";
import {
  CandidateSkillUpdateAction,
  DeleteSkills,
} from "../../../Redux/action/CandidateSkillUpdateAction";
import { fetchSkillDetails } from "../../../Redux/action/MasterSkillAction";

const PRIMARY_COLOR = "#286a94";

const Skills = ({ setActiveTab }) => {
  const dispatch = useDispatch();
  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state?.CandidateSkills?.result);
  const candidateId = userResult?.userid;
  const [skillsList, setSkillsList] = useState([]);
  const [showNewSkillFields, setShowNewSkillFields] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const AllSkillList = useSelector(
    (state) => state?.MasterTableDetails?.skill?.content,
  );
  const [newSkill, setNewSkill] = useState({
    skills: "",
    experience: "",
    rating: "",
  });

  const [editedSkill, setEditedSkill] = useState({
    skills: "",
    experience: "",
    durationValue: "",
    durationUnit: "",
    rating: "",
    index: -1,
  });
  const existingSkills = new Set(
    skillsList?.map((s) => s?.skillName || s?.skills),
  );

  useEffect(() => {
    dispatch(CandidateSkillAction(candidateId));
    dispatch(fetchSkillDetails());
  }, [dispatch]);

  useEffect(() => {
    if (userInfo) {
      setSkillsList(
        userInfo.map((skill) => ({
          candidateSkillsId: skill.candidateSkillsId,
          skills: skill.skills ?? "NA",
          experience: skill.experience ?? "NA",
          durationValue: skill.experience ? parseInt(skill.experience) : "NA",
          durationUnit: skill.experience
            ? skill.experience.includes("Years")
              ? "Years"
              : "Months"
            : "",
          rating: skill.rating ?? "NA",
        })),
      );
    }
  }, [userInfo]);

  const handleEditSkill = (index) => {
    const skillToEdit = skillsList[index];
    setEditedSkill({
      skills: skillToEdit.skills,
      experience: skillToEdit.experience,
      durationValue: skillToEdit.durationValue,
      durationUnit: skillToEdit.durationUnit,
      rating: skillToEdit.rating,
      index,
    });
    setEditMode(true);
  };

  const handleAddNewSkill = async () => {
    const { skills, rating, durationValue } = newSkill;
    console.log(newSkill);
    
    if (
      !skills ||
      !rating ||
      durationValue === "" ||
      Number(durationValue) <= 0
    ) {
      toast.error("Please enter valid skill details");
      return;
    }

    const content = {
      candidateId,
      skills,
      experience: `${durationValue} ${newSkill.durationUnit}`,
      rating,
    };

    try {
      await dispatch(AddSkillsAction(content));
      await dispatch(CandidateSkillAction(candidateId));

      setShowNewSkillFields(false);
      setNewSkill({ skills: "", durationValue: "", rating: "" });
    } catch (e) {
      console.log(e);
    }
  };

  const handleSaveEdit = async (candidateSkillsId) => {
    const { skills, durationValue, durationUnit, rating } = editedSkill;
    if (durationValue === "" || Number(durationValue) < 0 || !durationUnit) {
      toast.error("Please enter a valid experience and select a duration unit");
      return;
    }

    const content = {
      candidateSkillsId,
      skills,
      experience: `${durationValue} ${durationUnit}`,
      rating,
    };
    dispatch(CandidateSkillUpdateAction(content));
    setEditMode(false);
    setTimeout(() => dispatch(CandidateSkillAction(candidateId)), 1000);
  };

  const handleDeleteSkill = async (skillId) => {
    try {
      await dispatch(DeleteSkills(skillId));
      setTimeout(() => {
        dispatch(CandidateSkillAction(candidateId));
      }, 500);
    } catch (error) {
      console.error("Error deleting skill:", error);
      toast.error("Something went wrong! Please try again.");
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 0.5, sm: 1 },
            mb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: PRIMARY_COLOR,
              fontSize: { xs: "1rem", sm: "1.15rem" },
            }}
          >
            Skills & Ratings
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "white",
              backgroundColor: "#235a7f",
              px: 1.5,
              py: 0.5,
              borderRadius: 4,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              alignSelf: { xs: "flex-start", sm: "center" },
            }}
          >
            Total Skills: <strong>{skillsList?.length || 0}</strong>
          </Typography>
        </Box>

        <Divider sx={{ mb: 2, mt: 1 }} />

        {/* ADD Skill button */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowNewSkillFields(!showNewSkillFields)}
            startIcon={
              showNewSkillFields ? (
                <Close fontSize="small" />
              ) : (
                <Add fontSize="small" />
              )
            }
            sx={{
              bgcolor: "#286a94",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              "&:hover": {
                bgcolor: "#1f5576",
              },
            }}
          >
            {showNewSkillFields ? "Close" : "Add Skill"}
          </Button>
        </Box>
        {showNewSkillFields && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              border: "1px solid #dce7f1",
              borderRadius: "12px",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Skill Name */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={
                    AllSkillList?.filter(
                      (skill) => !existingSkills.has(skill?.skillName),
                    ) || []
                  }
                  getOptionLabel={(option) => option?.skillName || ""}
                  value={
                    AllSkillList?.find(
                      (s) => s.skillName === newSkill.skills,
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    setNewSkill({
                      ...newSkill,
                      skills: newValue ? newValue.skillName : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          Skill Name <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: "#286A94",
                        },
                        "& label.Mui-focused": {
                          color: "#286A94",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Experience Value */}
              <Grid item xs={6} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: "#286A94",
                    },
                    "& label.Mui-focused": {
                      color: "#286A94",
                    },
                  }}
                >
                  <InputLabel>
                    Experience <span style={{ color: "red" }}>*</span>
                  </InputLabel>

                  <Select
                    value={newSkill.durationValue}
                    label="Experience *"
                    onChange={(e) =>
                      setNewSkill({
                        ...newSkill,
                        durationValue: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>

                    {[...Array(30)].map((_, i) => (
                      <MenuItem key={i} value={i+1}>
                        {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Unit */}
              <Grid item xs={6} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: "#286A94",
                    },
                    "& label.Mui-focused": {
                      color: "#286A94",
                    },
                  }}
                >
                  <InputLabel>
                    Unit <span style={{ color: "red" }}>*</span>
                  </InputLabel>

                  <Select
                    value={newSkill.durationUnit}
                    label="Unit *"
                    onChange={(e) =>
                      setNewSkill({ ...newSkill, durationUnit: e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Select Unit</em>
                    </MenuItem>
                    <MenuItem value="Months">Months</MenuItem>
                    <MenuItem value="Years">Years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Rating */}
              <Grid item xs={12} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: "#286A94",
                    },
                    "& label.Mui-focused": {
                      color: "#286A94",
                    },
                  }}
                >
                  <InputLabel>
                    Rating <span style={{ color: "red" }}>*</span>
                  </InputLabel>

                  <Select
                    label="Rating *"
                    value={newSkill.rating}
                    onChange={(e) =>
                      setNewSkill({ ...newSkill, rating: e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Select Rating</em>
                    </MenuItem>
                    <MenuItem value="Excellent">Excellent</MenuItem>
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Average">Average</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Add Button */}
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  onClick={handleAddNewSkill}
                  fullWidth
                  size="small"
                  startIcon={<Add fontSize="small" />}
                  sx={{
                    height: 36,
                    background: "#286A94",
                    borderRadius: "8px",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      background: "#1E5477",
                    },
                  }}
                >
                  Add Skill
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* TABLE LIST */}
        <TableContainer
          component={Paper}
          sx={{ maxHeight: 330, overflow: "auto", width: "100%" }}
        >
          <Table stickyHeader sx={{ minWidth: { xs: "600px", sm: "auto" } }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>S.No</b>
                </TableCell>
                <TableCell>
                  <b>Skills</b>
                </TableCell>
                <TableCell>
                  <b>Experience</b>
                </TableCell>
                <TableCell>
                  <b>Rating</b>
                </TableCell>
                <TableCell>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {skillsList.length ? (
                skillsList.map((skill, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>

                    {editMode && editedSkill.index === index ? (
                      <>
                        {/* Skill Name */}
                        <TableCell
                          sx={{ verticalAlign: "middle", minWidth: 180 }}
                        >
                          <TextField
                            value={editedSkill.skills}
                            fullWidth
                            label="Skill Name"
                            size="small"
                            variant="outlined"
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                bgcolor: "#f8fafc",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e2e8f0",
                              },
                            }}
                          />
                        </TableCell>

                        {/* Duration */}
                        <TableCell
                          sx={{ verticalAlign: "middle", minWidth: 220 }}
                        >
                          <Grid container spacing={1} alignItems="center">
                            {/* Experience value */}
                            <Grid item xs={6}>
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                  },
                                  "& .MuiOutlinedInput-root.Mui-focused fieldset":
                                    {
                                      borderColor: "#286A94",
                                    },
                                  "& label.Mui-focused": {
                                    color: "#286A94",
                                  },
                                }}
                              >
                                <InputLabel>Experience</InputLabel>
                                <Select
                                  value={editedSkill.durationValue ?? ""}
                                  label="Experience"
                                  onChange={(e) =>
                                    setEditedSkill({
                                      ...editedSkill,
                                      durationValue: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select</em>
                                  </MenuItem>

                                  {[...Array(30)].map((_, i) => {
                                    const val = i + 1;
                                    return (
                                      <MenuItem key={val} value={val}>
                                        {val}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Unit */}
                            <Grid item xs={6}>
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                  },
                                  "& .MuiOutlinedInput-root.Mui-focused fieldset":
                                    {
                                      borderColor: "#286A94",
                                    },
                                  "& label.Mui-focused": {
                                    color: "#286A94",
                                  },
                                }}
                              >
                                <InputLabel>Unit</InputLabel>
                                <Select
                                  value={editedSkill.durationUnit}
                                  label="Unit"
                                  onChange={(e) =>
                                    setEditedSkill({
                                      ...editedSkill,
                                      durationUnit: e.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select</em>
                                  </MenuItem>
                                  <MenuItem value="Months">Months</MenuItem>
                                  <MenuItem value="Years">Years</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </TableCell>

                        {/* Rating */}
                        <TableCell
                          sx={{ verticalAlign: "middle", minWidth: 150 }}
                        >
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                              "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                                borderColor: "#286A94",
                              },
                              "& label.Mui-focused": {
                                color: "#286A94",
                              },
                            }}
                          >
                            <InputLabel id="rating-label">Rating</InputLabel>

                            <Select
                              labelId="rating-label"
                              label="Rating"
                              value={editedSkill.rating}
                              onChange={(e) =>
                                setEditedSkill({
                                  ...editedSkill,
                                  rating: e.target.value,
                                })
                              }
                            >
                              <MenuItem value="">
                                <em>Select Rating</em>
                              </MenuItem>
                              <MenuItem value="Excellent">Excellent</MenuItem>
                              <MenuItem value="Good">Good</MenuItem>
                              <MenuItem value="Average">Average</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>

                        {/* Actions */}
                        <TableCell
                          sx={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() =>
                                handleSaveEdit(skill.candidateSkillsId)
                              }
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>

                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => setEditMode(false)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{skill.skills}</TableCell>
                        <TableCell>{`${skill.durationValue} ${skill.durationUnit}`}</TableCell>
                        <TableCell>{skill.rating}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              sx={{ color: PRIMARY_COLOR }}
                              onClick={() => handleEditSkill(index)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              sx={{ color: "#ff0000" }}
                              onClick={() =>
                                handleDeleteSkill(skill.candidateSkillsId)
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No Skills Added
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          variant="outlined"
          size="small"
          sx={{
            mt: 3,
            color: "#286a94",
            borderColor: "#286a94",
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            "&:hover": {
              borderColor: "#1f5576",
              bgcolor: "rgba(40,106,148,0.08)",
            },
          }}
          onClick={() => setActiveTab("Profile")}
        >
          Back
        </Button>
      </Box>
    </>
  );
};

export default Skills;
